import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, Injector, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { delay } from 'rxjs/operators';
import { AssetFileInputComponent } from '../../../../shared/components/asset-file-input/asset-file-input.component';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Document, DocumentStatusType, JournalAttachedEntityType, TaskAttachedEntityType } from '../../../../generated/generated-types';
import { DocumentReportHistoryService } from '../../../../shared/services/report-history.service';
import { AssetService } from '../../../asset/services/asset.service';
import { WidgetAssetsManagerComponent } from '../../../asset/widget-assets-manager/widget-assets-manager.component';
import { JournalListWidgetComponent } from '../../../journal/components/journal-list-widget/journal-list-widget.component';
import { ReportPrintUIService } from '../../../printing/services/report-print-ui.service';
import { ReportContext } from '../../../printing/services/type-interfaces';
import { WidgetAssetListComponent } from '../../../products/components/widget-asset-list/widget-asset-list.component';
import { TaskListWidgetComponent } from '../../../task/components/task-list-widget/task-list-widget.component';
import { DocumentService } from '../../services/document.service';

@Component({
  selector: 'app-formly-document-assets-manager',
  templateUrl: './formly-document-assets-manager.component.html',
  styleUrls: ['./formly-document-assets-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AssetFileInputComponent,
    WidgetAssetListComponent,
    BlockableDiv,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    JournalListWidgetComponent,
    TaskListWidgetComponent,
  ]
})
export class FormlyDocumentAssetsManagerComponent extends WidgetAssetsManagerComponent<Document> {
  private readonly reportPrintUIService = inject(ReportPrintUIService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  documentReportHistoryService = inject(DocumentReportHistoryService);
  assetListWidget = viewChild(WidgetAssetListComponent);
  private readonly journalListWidget = viewChild<JournalListWidgetComponent>('journalListWidget');
  private readonly taskListWidget = viewChild<TaskListWidgetComponent>('taskListWidget');

  isSaving = signal(false);

  /** Last entity id we applied journal/task filters for — prevents refresh loops. */
  private attachedFiltersAppliedForId: string | null = null;

  get document() {
    return this.model as Document;
  }

  get isBlocked(): boolean {
    return !this.model?.id;
  }

  /** Viewport fill only while blocked — never tab fillHeight (that kills page scroll). */
  get blockableClass(): string {
    return this.isBlocked
      ? 'flex min-h-0 w-full flex-col'
      : 'w-full';
  }

  get blockedStyle(): Record<string, string> {
    return this.isBlocked
      ? { height: 'calc(100vh - 14rem)', minHeight: 'calc(100vh - 14rem)' }
      : {};
  }

  checkIfNoDelete(): boolean {
    if (!this.document) {
      return false;
    }
    return this.document.status === DocumentStatusType.CO || this.document.status === DocumentStatusType.RE;
  }

  constructor() {
    super(inject(DocumentService), inject(AssetService), inject(NotificationService), inject(I18nService));

    this.documentReportHistoryService.reportHistorySaved$.pipe(
      takeUntilDestroyed(this.destroyRef),
      delay(1000), // give other components time to save the asset or handle stuff
    ).subscribe((reportHistory) => {
      if (reportHistory && reportHistory.documentId === this.document?.id) {
        if (this.assetListWidget()?.initialFetchExecuted) {
          this.assetListWidget()?.refresh();
        }
      }
    });

    effect(() => {
      const journal = this.journalListWidget();
      const task = this.taskListWidget();
      if (!journal && !task) {
        return;
      }
      this.applyAttachedEntityFilters();
    });

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  ngAfterViewInit(): void {
    this.assetListWidget()?.emailButtonClick.subscribe((assets) => {
      const reportContext: ReportContext = {};
      reportContext.uploadedAssetIds = assets.map((asset) => asset.id);
      this.reportPrintUIService.showEmailDialog(reportContext, assets);
    });
  }

  checkIfCanSave(): boolean {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Document>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode(): Promise<void> {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Document>;
    if (!component) {
      console.error('No detail component found to save Document');
      return;
    }

    this.isSaving.set(true);
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving.set(false);
    this.attachedFiltersAppliedForId = null;
    this.cdRef.detectChanges();

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  private applyAttachedEntityFilters(): void {
    const documentId = this.document?.id;
    if (!documentId) {
      return;
    }

    const journal = this.journalListWidget();
    const task = this.taskListWidget();
    if (!journal && !task) {
      return;
    }

    if (this.attachedFiltersAppliedForId === documentId) {
      return;
    }

    journal?.setCustomSearchInput({
      attachedEntityType: JournalAttachedEntityType.DOCUMENT,
      attachedEntityId: documentId,
    });

    task?.setCustomSearchInput({
      attachedEntityType: TaskAttachedEntityType.DOCUMENT,
      attachedEntityId: documentId,
    });

    this.attachedFiltersAppliedForId = documentId;
  }
}
